/*
 * Copyright (C) 2021 Quartet Health, Inc - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import * as React from 'react';
import fonts, { fontSizes } from '@coop/ole/styles/fonts';
import Heading from '@coop/ole/Heading';
import spacing from '@coop/ole/styles/spacing';
import grid from '@coop/ole/styles/grid';
import styled from 'styled-components';
import { colors } from '@coop/ole/styles/colors';
import { TwitterIcon } from '@coop/ole/Icons/TwitterIcon';
import { LinkedinIcon } from '@coop/ole/Icons/LinkedinIcon';
import { FacebookIcon } from '@coop/ole/Icons/FacebookIcon';
import { InstagramIcon } from '@coop/ole/Icons/InstagramIcon';
import { AnchorLink } from '@coop/ole/AnchorLink';
import {
  FACEBOOK,
  FOR_PROVIDERS_MARKETING_URL,
  INSTAGRAM,
  LINKEDIN,
  PRIVACY_POLICY,
  SUPPORT_PHONE_NUMBER,
  TERMS_AND_CONDITIONS,
  TWITTER,
  SECURITY,
  NoticeOfNondiscriminationURL
} from '@coop/constants/generic';

import { t, Trans } from '@lingui/macro';

import { MarketContext } from 'src/config';
import { trackEvent, Action } from 'src/utils/tracking';
import { LinkTextCss } from 'src/components/fonts';

import stacking from 'src/utils/stacking';
import { breakpointsPhablet } from 'src/utils/customBreakpoints';
import { switchAssetBasedOnScreenSize } from 'src/utils/assetSwitcher';
import { WindowDimensions } from 'src/types/dimensions';
import SurfsUpWave, {
  WaveTypes
} from 'src/components/LandingPage/medicaid/WaveForms/SurfsUpWave';
import { PageProps } from 'src/components/Page';

const Event = {
  EmergencyResources: 'emergency-resources'
};

interface ComponentProps extends PageProps {
  stripped?: boolean;
}

interface StrippedFooterProps extends PageProps {
  className: string;
}

interface FooterProps
  extends StrippedFooterProps,
    WindowDimensions,
    ComponentProps {}

const FooterContainer = styled.footer<ComponentProps>`
  color: ${colors.grey100};
  padding: ${(props) => (props.stripped ? '0' : `${spacing.sm}`)} 0 0 0;
  @media (min-width: ${grid.breakpoints.lg}) {
    margin-top: ${(props) => (props.stripped ? '0' : `${spacing.xxxxl}`)};
  }
`;

const Content = styled.div`
  display: flex;
  flex-flow: column nowrap;
  padding: 0;

  @media (min-width: ${grid.breakpoints.lg}) {
    ${stacking.level2};
    flex-flow: row nowrap;
    justify-content: space-between;
    padding: 0 ${spacing.xl};
  }
`;

const Emergency = styled.div`
  ${stacking.level2};
  padding: ${spacing.lg} ${spacing.md};
  margin-top: ${spacing.xxl};

  @media (min-width: ${grid.breakpoints.xs}) {
    background-color: transparent;
  }

  @media (min-width: ${breakpointsPhablet}) {
    padding: ${spacing.lg} ${spacing.xl};
  }

  @media (min-width: ${grid.breakpoints.md}) {
    padding: ${spacing.xxl} ${spacing.xl} ${spacing.lg} ${spacing.xl};
  }

  @media (min-width: ${grid.breakpoints.lg}) {
    max-width: 450px;
    padding: 0 ${spacing.xxl} 0 0;
  }

  @media (min-width: ${grid.breakpoints.xl}) {
    max-width: 500px;
    margin-right: 100px;
    padding: 0;
  }
`;

const Bold = styled.span`
  font: ${fonts.bold(fontSizes.h6)};
`;

const Anchor = styled(AnchorLink)`
  width: fit-content;
  ${LinkTextCss}
  color: ${colors.grey100};
`;

const Resources = styled.div`
  display: flex;
  flex-flow: column nowrap;
  ${stacking.level2};
  background-color: transparent;
  padding: ${spacing.xxxl} ${spacing.md} 0 ${spacing.md};

  div:first-child {
    margin-right: ${spacing.xxxl};
  }

  @media (min-width: ${breakpointsPhablet}) {
    flex-flow: row nowrap;
    ${stacking.level2};
    background-color: transparent;
    padding: ${spacing.lg} ${spacing.xl} 0 ${spacing.xl};
    display: flex;
  }

  @media (min-width: ${grid.breakpoints.md}) {
    ${stacking.level2};
    padding: ${spacing.lg} ${spacing.xl};
    display: flex;
  }

  @media (min-width: ${grid.breakpoints.lg}) {
    flex-grow: 1;
    justify-content: space-between;
    background-color: transparent;
    margin-top: ${spacing.xxl};
    padding: 0;

    div:first-child {
      margin-right: ${spacing.xxl};
    }
  }

  @media (min-width: ${grid.breakpoints.xl}) {
    div:first-child {
      margin: 0 ${spacing.xxxl};
    }
  }
`;

const LinkList = styled.div`
  a {
    display: block;
    margin-bottom: ${spacing.sm};
  }

  @media (min-width: ${breakpointsPhablet}) {
    a:last-child {
      margin: 0;
    }

    flex: 1;
  }
`;

const SocialWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  ${stacking.level3};
  margin-top: ${spacing.xxl};

  > div:first-child {
    a {
      display: block;
      margin-bottom: ${spacing.sm};
    }
    a:last-child {
      margin-bottom: ${spacing.md};
    }
  }

  @media (min-width: ${breakpointsPhablet}) {
    margin-top: 0;
  }

  @media (min-width: ${grid.breakpoints.md}) {
    flex: 1;
  }

  @media (min-width: ${grid.breakpoints.lg}) {
    flex-grow: 1;
    justify-content: space-between;
    flex-flow: row nowrap;
  }
`;

const SocialIconWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  margin-top: ${spacing.xxl};

  @media (min-width: ${grid.breakpoints.lg}) {
    margin-top: 0;
  }
`;

const IconLink = styled(AnchorLink)`
  width: fit-content;
  height: fit-content;
  margin-right: ${spacing.lg};

  &:hover {
    opacity: 0.7;
  }
`;

const Copyright = styled.div<ComponentProps>`
  font: ${fonts.bold('14px')};
  line-height: 20px;
  padding: ${(props) =>
    props.stripped
      ? `${spacing.xl} ${spacing.md}`
      : `${spacing.xxxl} ${spacing.md}`};
  position: relative;
  background-color: ${(props) =>
    props.stripped ? 'auto' : `${colors.neutral01}`};
  color: ${colors.grey100};
  ${stacking.level1};

  @media (min-width: ${breakpointsPhablet}) {
    padding: ${spacing.xxxl} ${spacing.xl};
    padding: ${(props) =>
      props.stripped ? `${spacing.xl}` : `${spacing.xxxl} ${spacing.xl}`};
  }

  @media (min-width: ${grid.breakpoints.lg}) {
    ${stacking.level3};
  }
`;

const Copy = styled.div`
  font: ${fonts.normal(fontSizes.body)};
  line-height: 20px;
`;

const EmergencySection: React.FC<ComponentProps> = () => {
  const { copy } = React.useContext(MarketContext);

  return (
    <Emergency>
      <Heading
        size={2}
        data-test="footer.title"
        style={{
          lineHeight: '16px',
          letterSpacing: '1.8px',
          margin: `0 0 ${spacing.xxs} 0`,
          font: fonts.bold('12px')
        }}
      >
        {copy.footer.title}
      </Heading>
      <Copy style={{ marginBottom: spacing.xs }}>
        <span>{copy.footer.paragraph}</span>
        <Bold>
          &nbsp;
          {copy.footer.call}
        </Bold>
        .
      </Copy>
      <Copy>
        <Anchor
          onClick={() =>
            trackEvent(Event.EmergencyResources, Action.ClickThrough)
          }
          target="_blank"
          href={copy.footer.resourceUrl}
          data-test="footer.link-crisis-resource"
          aria-label={copy.footer.resourceLabel}
          rel="noopener noreferrer"
        >
          {copy.footer.resource}
        </Anchor>
        <span>
          &nbsp;
          {copy.footer.resourceHelp}
        </span>
      </Copy>
    </Emergency>
  );
};

const ResourcesSection: React.FC<ComponentProps> = ({
  hideContact,
  hideSocial,
  stripped
}) => {
  const { copy } = React.useContext(MarketContext);

  return (
    <Resources>
      <LinkList>
        <Anchor
          target="_blank"
          data-test="footer.link-privacy"
          href={PRIVACY_POLICY}
          aria-label={copy.footer.privacy}
          rel="noopener noreferrer"
        >
          {copy.footer.privacy}
        </Anchor>
        <Anchor
          target="_blank"
          data-test="footer.link-terms"
          href={TERMS_AND_CONDITIONS}
          aria-label={copy.footer.termsLabel}
          rel="noopener noreferrer"
        >
          {copy.footer.terms}
        </Anchor>
        <Anchor
          target="_blank"
          data-test="footer.link-security"
          href={SECURITY}
          aria-label={copy.footer.securityLabel}
          rel="noopener noreferrer"
        >
          {copy.footer.security}
        </Anchor>
        <Anchor
          target="_blank"
          data-test="footer.link-language"
          href={NoticeOfNondiscriminationURL}
          aria-label={copy.footer.languageLabel}
          rel="noopener noreferrer"
        >
          {copy.footer.language}
        </Anchor>
        <Anchor
          target="_blank"
          data-test="footer.link-provider"
          rel="noopener noreferrer"
          href={FOR_PROVIDERS_MARKETING_URL}
          aria-label={copy.footer.providerLabel}
        >
          {copy.footer.provider}
        </Anchor>
      </LinkList>
      {!hideSocial && !hideContact && (
        <SocialWrapper>
          {!hideContact && (
            <div>
              <Anchor
                href="mailto:help@quartethealth.com"
                data-test="footer.link-support-email"
              >
                {t`help@quartethealth.com`}
              </Anchor>
              <Anchor
                href={SUPPORT_PHONE_NUMBER.htmlFormatted}
                data-test="footer.link-support-phone-number"
                rel="nofollow"
              >
                {SUPPORT_PHONE_NUMBER.humanReadable}
              </Anchor>
            </div>
          )}
          {!hideSocial && (
            <SocialIconWrapper>
              <IconLink
                href={LINKEDIN}
                target="_blank"
                rel="noopener noreferrer"
                data-test="footer.link-linkedin-icon"
                aria-label={t`Visit us on LinkedIn`}
              >
                <LinkedinIcon color={colors.grey100} />
              </IconLink>
              <IconLink
                href={FACEBOOK}
                target="_blank"
                rel="noopener noreferrer"
                data-test="footer.link-facebook-icon"
                aria-label={t`Visit us on Facebook`}
              >
                <FacebookIcon color={colors.grey100} />
              </IconLink>
              <IconLink
                href={INSTAGRAM}
                target="_blank"
                rel="noopener noreferrer"
                data-test="footer.link-instagram-icon"
                aria-label={t`Visit us on Instagram`}
              >
                <InstagramIcon color={colors.grey100} />
              </IconLink>
              <IconLink
                href={TWITTER}
                target="_blank"
                rel="noopener noreferrer"
                data-test="footer.link-twitter-icon"
                aria-label={t`Visit us on Twitter`}
              >
                <TwitterIcon color={colors.grey100} />
              </IconLink>
            </SocialIconWrapper>
          )}
        </SocialWrapper>
      )}
    </Resources>
  );
};

const CopyrightSection: React.FC<ComponentProps> = (props) => (
  <Copyright {...props} data-test="footer.copyright">
    <Trans>
      © {new Date().getFullYear()} Quartet Health, Inc. All Rights Reserved.
    </Trans>
  </Copyright>
);

export const StrippedFooter: React.FC<StrippedFooterProps> = ({
  className,
  ...props
}) => (
  <FooterContainer className={className} stripped data-test="sr.footer">
    <Content>
      <EmergencySection stripped />
      <ResourcesSection {...props} stripped />
    </Content>
    <CopyrightSection stripped />
  </FooterContainer>
);

const Footer: React.FC<FooterProps> = ({ dimensions, className, ...props }) =>
  props.stripped ? (
    <StrippedFooter className={className} {...props} />
  ) : (
    <FooterContainer className={className} data-test="sr.footer">
      {switchAssetBasedOnScreenSize(dimensions.width, {
        SMElement: null,
        MDElement: null,
        LGElement: (
          <SurfsUpWave
            waveStyles={{
              type: WaveTypes.Simple,
              waveColor: colors.neutral02,
              zIndex: stacking.level1,
              shouldInvertWave: true,
              baseBreakpoint: {
                top: '-6vw',
                marginBottom: '-35vw',
                borderBottom: `26vw solid ${colors.neutral02}`
              }
            }}
            data-test="wave-1"
          />
        ),
        XLElement: (
          <SurfsUpWave
            waveStyles={{
              type: WaveTypes.Simple,
              waveColor: colors.neutral02,
              zIndex: stacking.level1,
              shouldInvertWave: true,
              baseBreakpoint: {
                top: '0vw',
                marginBottom: '-26vw',
                borderBottom: `26vw solid ${colors.neutral02}`
              }
            }}
            data-test="wave-1"
          />
        )
      })}
      <Content>
        {switchAssetBasedOnScreenSize(dimensions.width, {
          SMElement: (
            <SurfsUpWave
              waveStyles={{
                type: WaveTypes.Simple,
                waveColor: colors.neutral02,
                zIndex: stacking.level1,
                baseBreakpoint: {
                  top: '-1vw',
                  marginBottom: '-100vw',
                  borderBottom: `90vw solid ${colors.neutral02}`
                }
              }}
              data-test="wave-2"
            />
          ),
          MDElement: (
            <SurfsUpWave
              waveStyles={{
                type: WaveTypes.Simple,
                waveColor: colors.neutral02,
                zIndex: stacking.level1,
                baseBreakpoint: {
                  top: '0vw',
                  marginBottom: '-99vw',
                  borderBottom: `90vw solid ${colors.neutral02}`
                }
              }}
              data-test="wave-2"
            />
          ),
          LGElement: null
        })}
        <EmergencySection />
        {switchAssetBasedOnScreenSize(dimensions.width, {
          SMElement: (
            <SurfsUpWave
              waveStyles={{
                type: WaveTypes.Simple,
                waveColor: colors.neutral01,
                zIndex: stacking.level1,
                baseBreakpoint: {
                  top: '-5vw',
                  marginBottom: '-135vw',
                  borderBottom: `120vw solid ${colors.neutral01}`
                }
              }}
              data-test="wave-3"
            />
          ),
          PhabletElement: (
            <SurfsUpWave
              waveStyles={{
                type: WaveTypes.Simple,
                waveColor: colors.neutral01,
                zIndex: stacking.level1,
                baseBreakpoint: {
                  top: '-3vw',
                  marginBottom: '-35vw',
                  borderBottom: `30vw solid ${colors.neutral01}`
                }
              }}
              data-test="wave-3"
            />
          ),
          MDElement: (
            <SurfsUpWave
              waveStyles={{
                type: WaveTypes.Calm,
                waveColor: colors.neutral01,
                zIndex: stacking.level1,
                baseBreakpoint: {
                  top: '0vw',
                  marginBottom: '-35vw',
                  borderBottom: `30vw solid ${colors.neutral01}`
                }
              }}
              data-test="wave-3"
            />
          ),
          LGElement: null
        })}
        <ResourcesSection {...props} />
      </Content>
      {switchAssetBasedOnScreenSize(dimensions.width, {
        SMElement: null,
        MDElement: null,
        LGElement: (
          <SurfsUpWave
            waveStyles={{
              type: WaveTypes.Calm,
              waveColor: colors.neutral01,
              zIndex: stacking.level1,
              baseBreakpoint: {
                top: '-7vw',
                marginBottom: '-10vw',
                borderBottom: `5vw solid ${colors.neutral01}`
              }
            }}
            data-test="wave-4"
          />
        ),
        XLElement: (
          <SurfsUpWave
            waveStyles={{
              type: WaveTypes.Calm,
              waveColor: colors.neutral01,
              zIndex: stacking.level1,
              baseBreakpoint: {
                top: '-13vw',
                marginBottom: '-22vw',
                borderBottom: `15vw solid ${colors.neutral01}`
              }
            }}
            data-test="wave-4"
          />
        )
      })}
      <CopyrightSection />
    </FooterContainer>
  );

export default Footer;
